<template>
  <section class="dir-layout">
    <ReportsSidebar
      :activeTab="activeTabName"
      :lockedKeys="lockedTabKeys"
      @change-tab="onChangeTab"
    />

    <div class="dir-content">
      <TopBarTitleComponent title="Reportes" :showBackIcon="true">
        <template slot="right-area"></template>
      </TopBarTitleComponent>

      <!-- Mobile pill tabs -->
      <div
        class="dir-tabs d-lg-none"
        style="padding: 12px 16px 12px; overflow-x: auto; white-space: nowrap; display: flex; flex-wrap: nowrap;"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="dir-tab"
          :class="{ active: activeTabName === tab.key }"
          @click="onChangeTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="dir-content-body">
        <!-- Facturas vs Cotizaciones -->
        <div v-show="activeTabName === 'comparative'">
          <ReportUpgradeCard
            v-if="isTabLocked('comparative')"
            feature="bi_dashboard_full"
            title="Facturas vs Cotizaciones"
            desc="Compara facturas emitidas con cotizaciones en el tiempo. Disponible en el plan Crece o superior."
          />
          <QuotesToInvoicesComponent v-else isSearchYear />
        </div>

        <!-- Ventas productos -->
        <div v-show="activeTabName === 'products'">
          <ReportUpgradeCard
            v-if="isTabLocked('products')"
            feature="bi_dashboard_basic"
            title="Ventas por producto"
            desc="Disponible en el plan Lanza o superior."
          />
          <SalesProductList v-else />
        </div>

        <!-- IVA -->
        <div v-show="activeTabName === 'iva'">
          <ReportUpgradeCard
            v-if="isTabLocked('iva')"
            feature="bi_dashboard_basic"
            title="Reporte de IVA"
            desc="Disponible en el plan Lanza o superior."
          />
          <SalesIvaList v-else />
        </div>

        <!-- Facturas -->
        <div v-show="activeTabName === 'invoices'">
          <ReportUpgradeCard
            v-if="isTabLocked('invoices')"
            feature="bi_dashboard_basic"
            title="Listado de facturas"
            desc="Disponible en el plan Lanza o superior."
          />
          <InvoicesList v-else />
        </div>

        <!-- Cobros -->
        <div v-show="activeTabName === 'collections'">
          <ReportUpgradeCard
            v-if="isTabLocked('collections')"
            feature="bi_dashboard_basic"
            title="Reporte de cobros"
            desc="Disponible en el plan Lanza o superior."
          />
          <CollectionsTab v-else />
        </div>

        <!-- Inteligencia de clientes -->
        <div v-show="activeTabName === 'client-intelligence'">
          <ReportUpgradeCard
            v-if="isTabLocked('client-intelligence')"
            feature="bi_dashboard_full"
            title="Inteligencia de clientes"
            desc="Scoring de recompra y análisis de comportamiento de clientes. Disponible en el plan Crece o superior."
          />
          <div v-else class="report-card mb-3">
            <div class="report-card-header">
              <h6 class="report-card-title">Scoring de clientes</h6>
              <small class="text-muted"
                >Probabilidad de recompra basada en historial de
                facturación</small
              >
            </div>
            <ClientScoring />
          </div>
        </div>

        <!-- Top servicios -->
        <div v-show="activeTabName === 'top-services'">
          <ReportUpgradeCard
            v-if="isTabLocked('top-services')"
            feature="bi_dashboard_full"
            title="Top servicios"
            desc="Servicios con mayor facturación en el período. Disponible en el plan Crece o superior."
          />
          <div v-else class="report-card">
            <div class="report-card-header">
              <h6 class="report-card-title">Top servicios</h6>
              <small class="text-muted"
                >Servicios con mayor facturación en el período</small
              >
            </div>
            <TopProducts period="12m" />
          </div>
        </div>

        <!-- Gastos -->
        <div v-show="activeTabName === 'expenses'">
          <ReportUpgradeCard
            v-if="isTabLocked('expenses')"
            feature="bi_dashboard_basic"
            title="Gestión de gastos"
            desc="Disponible en el plan Lanza o superior."
          />
          <template v-else>
            <div class="expenses-subtabs">
              <button
                class="expenses-subtab"
                :class="{ active: expensesSubTab === 'reporting' }"
                @click="expensesSubTab = 'reporting'"
              >
                Reportería
              </button>
              <button
                class="expenses-subtab"
                :class="{ active: expensesSubTab === 'list' }"
                @click="expensesSubTab = 'list'"
              >
                Listado
              </button>
            </div>
            <div v-show="expensesSubTab === 'reporting'">
              <ExpensesReport />
            </div>
            <div v-show="expensesSubTab === 'list'"><GastosListView /></div>
          </template>
        </div>
      </div>
    </div>

    <!-- Modal: Mensaje masivo (usado por ClientScoring) -->
    <BulkMessageModal
      :visible.sync="showBulkMessageModal"
      :recipients="bulkMessageRecipients"
      :context="bulkMessageContext"
      @sent="showBulkMessageModal = false"
    />
  </section>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import ReportsSidebar from "../components/ReportsSidebar";
import QuotesToInvoicesComponent from "../components/QuotesToInvoices";
import SalesProductList from "../components/SalesProductList";
import SalesIvaList from "../components/SalesIvaList";
import InvoicesList from "../components/InvoicesList.vue";
import ClientScoring from "../components/ClientScoring";
import TopProducts from "../components/TopProducts";
import CollectionsTab from "../../sales/pages/projects/components/CollectionsTab";
import BulkMessageModal from "../components/BulkMessageModal";
import ExpensesReport from "../components/ExpensesReport";
import GastosListView from "../../sales/pages/expenses/GastosListView";
import ReportUpgradeCard from "../components/ReportUpgradeCard";

const VALID_TABS = [
  "comparative",
  "products",
  "iva",
  "invoices",
  "collections",
  "client-intelligence",
  "top-services",
  "expenses"
];

// Feature requerida por cada tab (null = siempre visible)
const TAB_FEATURE_MAP = {
  comparative: "bi_dashboard_full",
  products: "bi_dashboard_basic",
  iva: "bi_dashboard_basic",
  invoices: "bi_dashboard_basic",
  collections: "bi_dashboard_basic",
  "client-intelligence": "bi_dashboard_full",
  "top-services": "bi_dashboard_full",
  expenses: "bi_dashboard_basic"
};

export default {
  components: {
    TopBarTitleComponent,
    ReportsSidebar,
    QuotesToInvoicesComponent,
    SalesProductList,
    SalesIvaList,
    InvoicesList,
    ClientScoring,
    TopProducts,
    CollectionsTab,
    BulkMessageModal,
    ExpensesReport,
    GastosListView,
    ReportUpgradeCard
  },
  data() {
    return {
      activeTabName: "comparative",
      expensesSubTab: "reporting",
      showBulkMessageModal: false,
      bulkMessageRecipients: [],
      bulkMessageContext: "clients"
    };
  },
  computed: {
    hasBIBasic() {
      return this.$store.getters.hasFeature("bi_dashboard_basic");
    },
    hasBIFull() {
      return this.$store.getters.hasFeature("bi_dashboard_full");
    },
    lockedTabKeys() {
      return Object.entries(TAB_FEATURE_MAP)
        .filter(([, feature]) => !this.$store.getters.hasFeature(feature))
        .map(([key]) => key);
    },
    tabs() {
      return [
        { key: "comparative", label: "Facturas vs Cotizaciones" },
        { key: "products", label: "Ventas productos" },
        { key: "iva", label: "IVA" },
        { key: "invoices", label: "Facturas" },
        { key: "collections", label: "Cobros" },
        { key: "expenses", label: "Gastos" },
        { key: "client-intelligence", label: "Inteligencia de clientes" },
        { key: "top-services", label: "Top servicios" }
      ].map(t => ({ ...t, locked: this.lockedTabKeys.includes(t.key) }));
    }
  },
  mounted() {
    // Si el tab por defecto está bloqueado, ir al primero disponible
    const tab = this.$route.query.tab;
    if (tab && VALID_TABS.includes(tab)) {
      this.activeTabName = tab;
    } else if (this.isTabLocked(this.activeTabName)) {
      const first = this.tabs.find(t => !t.locked);
      if (first) this.activeTabName = first.key;
    }
    this.$root.$on("open-bulk-message", this.onBulkMessage);
  },
  beforeDestroy() {
    this.$root.$off("open-bulk-message", this.onBulkMessage);
  },
  watch: {
    "$route.query.tab"(tab) {
      if (tab && VALID_TABS.includes(tab)) {
        this.activeTabName = tab;
      }
    }
  },
  methods: {
    isTabLocked(key) {
      return this.lockedTabKeys.includes(key);
    },
    onChangeTab(tab) {
      this.activeTabName = tab;
    },
    onBulkMessage({ recipients, context }) {
      this.bulkMessageRecipients = recipients;
      this.bulkMessageContext = context || "clients";
      this.showBulkMessageModal = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.expenses-subtabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 2px solid #e4eaf6;
  padding-bottom: 0;
}
.expenses-subtab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 500;
  color: #8898aa;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: #2d2b55;
  }
  &.active {
    color: #3ecfb4;
    border-bottom-color: #3ecfb4;
  }
}
.report-card {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  padding: 16px 18px;
}
.report-card-header {
  margin-bottom: 14px;
}
.report-upgrade-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  background: #fff;
  border: 1.5px dashed #d0ede8;
  border-radius: 12px;
  gap: 10px;

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #f0faf7;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0;
  }

  &__desc {
    font-size: 13px;
    color: #888780;
    margin: 0;
    max-width: 380px;
    line-height: 1.5;
  }

  &__btn {
    margin-top: 6px;
    padding: 9px 22px;
    background: #2d2b55;
    color: #fff;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.15s;

    &:hover {
      background: #3ecfb4;
      color: #fff;
      text-decoration: none;
    }
  }
}

.report-card-title {
  font-size: 16px;
  font-weight: 500;
  color: #31355c;
  margin: 0;
  text-transform: none;
  letter-spacing: normal;
}
</style>
